import React, {
  memo,
  useMemo,
  useEffect,
  useCallback,
  useState,
  FocusEvent,
  ChangeEvent,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'
import { IOption } from 'components/forms/combobox/types'
import orderBy from 'lodash/orderBy'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import PeopleIcon from 'transfix-icons/build/PeopleIcon'
import PlusIcon from 'transfix-icons/build/PlusIcon'
import {
  Autocomplete,
  getOptionGroupName,
  IAutocompleteOption,
} from 'transfix-ui/components/Autocomplete'
import {
  EMPTY_AUTOCOMPLETE_VALUE,
  getOptionWithParams,
  UNASSIGNED_VALUE,
  ADD_OPTION_VALUE,
  getAutosuggestOptions,
} from 'transfix-ui/components/Autosuggest'
import { IconButton } from 'transfix-ui/components/IconButton'
import {
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  FilterOptionsState,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  createFilterOptions,
} from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useInputState } from 'utils/hooks/useInputState'
import { IDriver } from 'apps/fleet-planner/types'
import { fetchDrivers } from 'apps/fleet-planner/utils/actions/drivers'
import { selectActiveDrivers } from 'apps/fleet-planner/utils/selectors'
import { Avatar, Tooltip } from 'transfix-ui'

export const createAutocompleteOption = (
  id: string,
  text: string,
  groupIndex: number,
  params?: Partial<IAutocompleteOption>
): IAutocompleteOption => ({
  id: `${id}`,
  text,
  groupIndex,
  ...params,
})

// index used to sort group order
const groupIndices = {
  unassigned: 1,
  addOption: 2,
  drivers: 3,
  driversForThisCarrier: 4,
  allDrivers: 5,
  addOptionWithValue: 6,
}

const unassignedOption = getOptionWithParams(UNASSIGNED_VALUE, {
  groupIndex: groupIndices.unassigned,
})

export const getGroupIndex = (groupName: keyof typeof groupIndices): number => {
  return groupIndices[groupName]
}

export const UNASSIGNED = createAutocompleteOption('-2', 'Unassigned', groupIndices.unassigned)

const filter = createFilterOptions<IAutocompleteOption>()

export const getDriverById = (
  driversList: IDriver[] = [],
  id: IDriver['id']
): IDriver | undefined => {
  if (!id) return undefined
  return driversList.find(({ id: driverId }) => `${driverId}` === `${id}`)
}

export const isNewDriverId = (driversList: IDriver[] = [], id: IDriver['id']): boolean => {
  return !!getDriverById(driversList, id)
}

export const getDriverByName = (driversList: IDriver[] = [], name: string): IDriver | undefined => {
  return driversList.find(d => `${d?.firstName} ${d?.lastName}` === `${name}`)
}

export const isNewDriverName = (driversList: IDriver[] = [], name: string): boolean => {
  if (!name) return true
  return !getDriverByName(driversList, name)
}

interface IDriverAutosuggestProps {
  /**
   * Default initial value. Used with uncontrolled state.
   */
  defaultValue?: IDriver | null | undefined
  /**
   * Filter these results out
   */
  currentDrivers?: (IDriver | null)[]
  /**
   * Label prop used for <TextInput />
   */
  label?: string
  /**
   * Input size
   * @default true
   */
  large?: boolean
  /**
   * Should dispatch fetch action on component mount
   */
  shouldFetchList?: boolean
  /**
   * Whether the first or second driver form is active
   */
  formIndex: number
  /**
   *
   */
  showUnassignedSuggestion?: boolean
  /**
   * Handles selections and sends value to parent component
   */
  onChange: (change: Partial<IDriver> | undefined, formIndex: number) => void
  /**
   * Toggles settings in parent component for driver form
   */
  setDriverForm: (isDriverFormVisible: boolean, formIndex: number, name?: string) => void
  /**
   * Automatically focus on input element
   */
  autoFocus?: boolean
  /**
   * Disables input
   */
  disabled?: boolean
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  error?: string
  /**
   * If `true`, the input's text will be cleared on blur if no value is selected.
   *
   * Set to `true` if you want to help the user enter a new value.
   * Set to `false` if you want to help the user resume his search.
   * @default false
   */
  clearOnBlur?: boolean
  /**
   * If `true`, clear all values when the user presses escape and the popup is closed.
   * @default false
   */
  clearOnEscape?: boolean
  /**
   * If `true`, the selected option becomes the value of the input
   * when the Autocomplete loses focus unless the user chooses
   * a different option or changes the character string in the input.
   * @default false
   */
  autoSelect?: boolean
  /**
   * If `true`, the first option is automatically highlighted.
   * @default false
   */
  autoHighlight?: boolean
  /**
   * Toggles parent form state to show another row of <DriverAutosuggest />
   */
  onAddAnotherDriver?: (formIndex: number) => void
  /**
   * Show or hide the "add driver 2" button
   */
  isAddButtonVisible?: boolean
  isRemoveButtonVisible?: boolean
  /**
   * Shows/hides "Add Driver" option in menu
   * @default true
   */
  isAddOptionVisible?: boolean
  hideAllButtons?: boolean
  /**
   * @default false
   */
  required?: boolean
  onToggleShow?: (show: boolean) => void
  onValidationChange?: (val?: string) => void
  /**
   * Filters drivers into two groups:
   * 1. Drivers that are part of user's carriers
   * 2. Other drivers
   * @default false
   */
  selectedMotorCarrierId?: number
}

export const DriverAutosuggest = memo<IDriverAutosuggestProps>(
  ({
    currentDrivers = [],
    label,
    onChange,
    onToggleShow,
    shouldFetchList: fetchList = true,
    large: largeProp = true,
    formIndex,
    setDriverForm,
    defaultValue,
    autoFocus,
    disabled,
    onBlur: onBlurProp,
    error: errorProp,
    clearOnBlur: clearOnBlurProp = false,
    clearOnEscape: clearOnEscapeProp = false,
    autoSelect: autoSelectProp = false,
    autoHighlight: autoHighlightProp = false,
    onAddAnotherDriver: onAddAnotherDriverProp,
    showUnassignedSuggestion = true,
    isAddButtonVisible,
    isRemoveButtonVisible,
    hideAllButtons,
    onValidationChange: onValidationChangeProp,
    required: requiredProp = false,
    isAddOptionVisible = true,
    selectedMotorCarrierId,
  }) => {
    const dispatch = useDispatch()
    const driversList = useSelector(selectActiveDrivers)

    // array of driver id's that should be excluded from results
    const alreadySelectedDrivers = useMemo(
      () => (currentDrivers ? currentDrivers.map(d => `${d?.id}`) : []),
      [currentDrivers]
    )

    const { isMobile } = useBreakpointContext()

    const [inputValue, setInputValue] = useState<string>('')

    const { getErrorMessage, onBlurTrigger, onChangeTrigger } = useInputState()

    const getCurrentValue = useCallback(
      (val: IDriver | undefined | null): IAutocompleteOption | undefined => {
        const d = getDriverById(driversList, val?.id)
        if (val?.id && d?.id) {
          return createAutocompleteOption(
            d?.id,
            `${d.firstName} ${d.lastName}`,
            groupIndices.drivers
          )
        }

        return undefined
      },
      [driversList]
    )

    const getAddOption = (inputVal: string): IAutocompleteOption => {
      const { addOption, addOptionWithValue } = groupIndices
      const isValidInput = !!inputVal && !inputVal.toLowerCase().includes('unassigned')
      return getOptionWithParams(ADD_OPTION_VALUE, {
        inputValue: inputVal,
        text: `Add ${isValidInput ? `"${inputVal}"` : 'New Driver'}`,
        groupIndex: inputVal ? addOptionWithValue : addOption,
      })
    }

    const addOption = useMemo(() => getAddOption(inputValue), [inputValue])

    /**
     * Only used on initial load to inform the default initial state
     */
    const getValue = useCallback(
      (val: IDriver | undefined | null): IAutocompleteOption | undefined => {
        const driver = getCurrentValue(val)
        if (driver) return driver

        if (val?.id === unassignedOption.id) {
          return unassignedOption
        }

        if (val?.id === addOption.id) {
          return addOption
        }

        return EMPTY_AUTOCOMPLETE_VALUE
      },
      [addOption, getCurrentValue]
    )

    // Get initial value and store in state
    const [initialValue, setInitialValue] = useState<IAutocompleteOption | undefined>(
      getValue(defaultValue)
    )

    // Update if a new/different initial prop is passed in
    // Should only run when defaultValue changes
    useEffect(() => {
      setInitialValue(getValue(defaultValue))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue])

    // Fetch drivers on mount, if `fetchList` is true
    useEffect(() => {
      if (fetchList && !driversList?.length) dispatch(fetchDrivers())
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Names keyed by index (group name)
    const optionGroupNames = useMemo(() => {
      return {
        [groupIndices.drivers]: 'Drivers',
        [groupIndices.driversForThisCarrier]: 'Drivers for this carrier',
        [groupIndices.allDrivers]: 'All Drivers and Owner Operators',
      }
    }, [])

    /**
     * Filter the groups of drivers into two arrays programmatically
     * and without running .filter() twice
     */
    const getDriverGroups = useCallback(() => {
      const carrierDrivers: IDriver[] = []
      const otherDrivers: IDriver[] = []

      for (const driver of driversList) {
        const isPreviouslySelected = alreadySelectedDrivers.includes(`${driver.id}`)

        // If the motor carrier is the same as the selected one
        if (!isPreviouslySelected && selectedMotorCarrierId === driver.motorCarrierId) {
          carrierDrivers.push(driver)
        } else if (!isPreviouslySelected) {
          otherDrivers.push(driver)
        }
      }

      return {
        carrierDrivers,
        otherDrivers,
      }
    }, [alreadySelectedDrivers, driversList, selectedMotorCarrierId])

    const allDrivers: IAutocompleteOption[] = useMemo(() => {
      if (!selectedMotorCarrierId) {
        return getAutosuggestOptions({
          options: driversList.filter(d => !alreadySelectedDrivers.includes(`${d.id}`)),
          groupIndex: groupIndices.drivers,
          groupName: optionGroupNames[groupIndices.drivers],
          inputValue,
          mapOption: ({ id, name: text = '' }) => ({
            id: `${id}`,
            text,
          }),
        })
      }

      /**
       * Only trigger this method if `filterByCarrier` is true,
       * since it requires some mapping of potentially many entries
       */
      const { carrierDrivers = [], otherDrivers = [] } = getDriverGroups()

      // <Autocomplete /> groups each array item by `groupIndex`
      const carrierDriverList = getAutosuggestOptions({
        options: carrierDrivers,
        groupIndex: groupIndices.driversForThisCarrier,
        groupName: optionGroupNames[groupIndices.driversForThisCarrier],
        inputValue,
        mapOption: ({ id, name: text = '' }) => ({
          id: `${id}`,
          text,
        }),
      })

      const otherDriverList = getAutosuggestOptions({
        options: otherDrivers,
        groupIndex: groupIndices.allDrivers,
        groupName: optionGroupNames[groupIndices.allDrivers],
        inputValue,
        mapOption: ({ id, name: text = '' }) => ({
          id: `${id}`,
          text,
        }),
      })

      return [...otherDriverList, ...carrierDriverList]
    }, [
      alreadySelectedDrivers,
      driversList,
      getDriverGroups,
      inputValue,
      optionGroupNames,
      selectedMotorCarrierId,
    ])

    // When clearing the input, also hide the form
    const clearForm = () => {
      // Sets `drivers[formIndex]` to undefined
      onChange(undefined, formIndex)
      setDriverForm(false, formIndex, undefined)
    }

    /**
     * Handles selecting `unassigned`, `add new driver` or a valid optoin
     */
    const selectOption = (details: AutocompleteChangeDetails<IOption> | undefined) => {
      const { option: selectedOption } = details || {}
      const { inputValue: inputVal = '', text } = selectedOption || {}

      if (text?.includes('Add')) {
        const isNewDriver = isNewDriverName(driversList, inputVal)
        const newFormValue = isNewDriver ? inputVal : undefined
        setDriverForm(true, formIndex, newFormValue)
      } else if (text?.includes('Unassigned')) {
        onChange(undefined, formIndex)
        setDriverForm(false, formIndex, undefined)
      } else if (selectedOption) {
        const selectedDriver = getDriverById(driversList, selectedOption?.id)
        setDriverForm(false, formIndex, selectedDriver?.name)
        onChange({ ...selectedDriver }, formIndex)
      }
    }

    /**
     * Handles option selection onClick
     */
    const optionSelectHandler = (
      event: React.ChangeEvent<{}>,
      val: any,
      reason: AutocompleteChangeReason,
      details: AutocompleteChangeDetails<IOption> | undefined
    ) => {
      onChangeTrigger()

      // Since `event` is typed with an object, we have to work
      // around type. But we can be sure it's from an input with
      // an `target.id` prop in this instance
      if (reason === 'clear' && !!(event?.target as any)?.id) {
        /**
         * No op!
         *
         * Because of the patterns of interaction with <TextInput /> within <Autocomplete />,
         * a "clear" action (onClearButtonClick) triggers both onClearButtonClick as well as onChange
         * and ends up causing a race condition in this specific case.
         *
         * To avoid causing 2 updates to the parent state, we can ignore the `clear`
         * and instead just rely on the empty value update, which is triggered also.
         */
      } else if (reason === 'blur') {
        clearForm()
      } else if (reason === 'selectOption') {
        selectOption(details)
      }
    }

    /**
     * Filter options
     */
    const filterOptions = (
      opts: IAutocompleteOption[],
      params: FilterOptionsState<IAutocompleteOption>
    ) => {
      const filtered = filter(opts, params)
      const { inputValue: inputVal } = params || {}

      // Add Option (always include)
      if (isAddOptionVisible) {
        filtered.push(getAddOption(inputVal))
      }

      // Unassigned (include if blank input, or already present)
      if (showUnassignedSuggestion && inputVal === '' && inputVal.toLowerCase() !== 'unassigned') {
        filtered.push(unassignedOption)
      }

      return orderBy(filtered, 'groupIndex')
    }

    /**
     * Render group with conditional class if it contains `unassigned` or `addOption`.
     * Groups are ordered by `groupIndex` value, which is an integer
     */
    const renderGroup = ({ children, group, key }: AutocompleteRenderGroupParams) => {
      const {
        addOption: addOptionGroup,
        addOptionWithValue: addOptionWithValueGroup,
        unassigned: unassignedGroup,
      } = groupIndices

      const isAddOptionGroup =
        group === `${addOptionGroup}` || group === `${addOptionWithValueGroup}`
      const isUnassignedGroup = group === `${unassignedGroup}`

      const classes = classNames('autocomplete-group', {
        'autocomplete-group-option-link': isAddOptionGroup || isUnassignedGroup,
      })

      return (
        <div key={key} className={classes} role='group' aria-label={group}>
          {!isAddOptionGroup && !isUnassignedGroup && (
            <p className='autocomplete-group-label'>
              {getOptionGroupName(group, optionGroupNames)}
            </p>
          )}
          {children}
        </div>
      )
    }

    /**
     * Render option with an Avatar or User icon, depending on value
     */
    const renderOption = (
      option: IOption,
      state: AutocompleteRenderOptionState,
      markup?: React.ReactNode
    ) => {
      const { text = '' } = option || {}
      return (
        <div className='flex autocomplete-option-avatar'>
          <div className='flex mr2'>
            {text?.includes('Add ') ? (
              <PlusIcon size='large' color='skyBlue6' />
            ) : (
              <Avatar key={option.text} name={text} size='input' hideTooltip />
            )}
          </div>
          <div className='flex-auto tl'>{markup}</div>
        </div>
      )
    }

    /**
     * Creates avatar icon next to name inside <TextInput />
     */
    const startAdornment = (currentInputValue: IOption | null) => {
      const { text } = currentInputValue || {}

      let avatar = (
        <span className='avatar-wrapper'>
          <Avatar name={text} size='input' hideTooltip />
        </span>
      )

      if (text?.includes('Add ')) {
        avatar = <PlusIcon size='large' className='input-icon input-icon-green' />
      }

      return avatar
    }

    const getOptionLabel = (option: IOption): string => {
      // Value selected with enter, right from the input
      if (typeof option === 'string') {
        return option
      }

      // Add "xxx" option created dynamically
      if (option?.inputValue) {
        return `Add "${option.inputValue}"`
      }

      // Regular option
      return option?.text
    }

    /**
     * Compares options with selected value. Returns true if it is selected option
     */
    const isOptionEqualToValue = (opt: IOption, val: IOption) => {
      return `${opt?.id}` === `${val?.id}`
    }

    /**
     * Generate error message based on conditions.
     * When any is `false`, the error will be triggered
     */
    const shouldValidate = inputValue?.toLowerCase() !== 'unassigned'
    const error = shouldValidate
      ? getErrorMessage('No matching drrivers found. Please select a driver or add a new one.', {
          inputStates: ['isDirty', 'isBlurred'],
          conditions: [
            !driversList.some(
              d => d.name?.toLowerCase().trim() === inputValue.toLowerCase().trim()
            ),
            !inputValue?.toLowerCase().includes('add '),
            !inputValue?.toLowerCase().includes('unassigned'),
          ],
        })
      : ''

    // When error updates, send to parent component
    useEffect(() => {
      if (onValidationChangeProp) onValidationChangeProp(error)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error])

    const inputChangeHandler = (event: ChangeEvent<any>, v: string = '') => {
      onChangeTrigger()
      setInputValue(v)
    }

    const blurHandler = (e: FocusEvent<HTMLInputElement>) => {
      onBlurTrigger()
      if (onBlurProp) onBlurProp(e)
    }

    const addAnotherDriverClickHandler = () => {
      if (onAddAnotherDriverProp) onAddAnotherDriverProp(formIndex)
    }

    const removeDriverHandler = () => {
      onChange(undefined, formIndex)
      if (onToggleShow) onToggleShow(false)
    }

    return (
      <div className='flex items-start'>
        <div className='flex-auto mr2'>
          <Autocomplete
            id={`driver-${formIndex}`}
            options={allDrivers}
            startAdornment={startAdornment}
            label={label}
            filterOptions={filterOptions}
            getOptionLabel={getOptionLabel}
            placeholder='Select a Driver'
            isLoading={false}
            large={largeProp}
            onChange={optionSelectHandler}
            dataTest={`driver-${formIndex}-autosuggest`}
            optionGroupNames={optionGroupNames}
            groupBy={opt => `${opt.groupIndex}`}
            renderGroup={renderGroup}
            renderOption={renderOption}
            isOptionEqualToValue={isOptionEqualToValue}
            defaultValue={initialValue}
            clearable
            disabled={disabled}
            autoFocus={autoFocus}
            onBlur={blurHandler}
            error={errorProp || error}
            clearOnBlur={clearOnBlurProp}
            clearOnEscape={clearOnEscapeProp}
            autoSelect={autoSelectProp}
            autoHighlight={autoHighlightProp}
            onInputChange={inputChangeHandler}
            limitOptions={6}
            selectOnFocus={!isMobile}
            required={requiredProp}
          />
        </div>
        {isAddButtonVisible && currentDrivers.length < 2 && (
          <div className='flex-shrink' style={{ paddingTop: '20px' }}>
            <Tooltip
              position='top'
              type='dark'
              effect='solid'
              trigger={
                <IconButton
                  color='tertiary'
                  Icon={PeopleIcon}
                  onClick={addAnotherDriverClickHandler}
                  data-test='add-second-driver'
                  label={`Add Driver ${formIndex + 1}`}
                  size={largeProp ? 'large' : 'medium'}
                />
              }>
              Add Driver {formIndex + 1}
            </Tooltip>
          </div>
        )}
        {isRemoveButtonVisible && (
          <div className='flex-shrink' style={{ paddingTop: '20px' }}>
            <Tooltip
              position='top'
              type='dark'
              effect='solid'
              trigger={
                <IconButton
                  color='tertiary'
                  Icon={DeleteIcon}
                  onClick={removeDriverHandler}
                  label={`Remove driver ${formIndex}`}
                  size={largeProp ? 'large' : 'medium'}
                />
              }>
              Remove Driver {formIndex}
            </Tooltip>
          </div>
        )}
        {!isRemoveButtonVisible && !isAddButtonVisible && !hideAllButtons && (
          <div className='flex-shrink' style={{ opacity: '0' }}>
            <IconButton
              color='tertiary'
              Icon={PeopleIcon}
              disabled
              label='no op'
              size={largeProp ? 'large' : 'medium'}
            />
          </div>
        )}
      </div>
    )
  }
)
